import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { USER_FILES_DEFAULT_SORT_COLUMN, USER_FILES_DEFAULT_SORT_COLUMN_DIRECTION, USER_FILES_PAGE_DEFAULT_SIZE, USER_FILES_PAGE_SIZE_OPTIONS } from 'src/app/conf/user-files';
import { UserFile } from 'src/app/model/user-file';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { pageSortChange } from 'src/app/services/table';
import { selectBoardUserLists, selectBoardUserPaging, selectLoading } from 'src/app/store/user-file/user-file.selector';
import { Tooltip } from 'primeng/tooltip';
import { selectBoardUsersTaskRunning, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { DeviceStatus } from 'src/app/model/device-status';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { ImportFileDialogComponent } from '../import-file-dialog/import-file-dialog.component';
import { ConfirmationService } from 'primeng/api';
import { BoardUsersFilesDialogComponent } from '../board-users-files-dialog/board-users-files-dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-board-users-files-list',
	templateUrl: './board-users-files.component.html',
	providers: []
})

export class BoardUsersFileComponent implements OnInit, OnDestroy {

	files: UserFile[];
	files$: Observable<UserFile[] | undefined>;
	filesSub: Subscription | undefined;

	filesLoading: boolean;
	filesLoading$: Observable<boolean | undefined>;
	filesLoadingSub: Subscription | undefined;

	boardUserPaging$: Observable<ItemsPagination | undefined>;
	boardUserPagingSub: Subscription | undefined;

	pageSortSub: Subscription | undefined;
	paging: PaginationParams = {
		size: USER_FILES_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: USER_FILES_DEFAULT_SORT_COLUMN,
		order: USER_FILES_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSizeOptions = USER_FILES_PAGE_SIZE_OPTIONS;
	pageSize = USER_FILES_PAGE_DEFAULT_SIZE;
	resultLength: number;

	taskRunning: boolean;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;

	offline: boolean;
	offline$: Observable<DeviceStatus>;
	offlineSub: Subscription | undefined;

	totalRecords = 0;

	sort: any;
	// paginator: any;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.getBoardUserList();

		this.onResize();
	}

	initSelectors(): void {
		this.filesLoading$ = this.store.select(selectLoading);
		this.files$ = this.store.select(selectBoardUserLists);
		this.boardUserPaging$ = this.store.select(selectBoardUserPaging);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
		this.offline$ = this.store.select(selectCurrentDeviceStatus);
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	getColumnTitle(): boolean {
		return (this.screenWidth <= this.COLUMN_TITLE_MIN_WD);
	}

	subscribeAll(): void {
		this.filesLoadingSub = this.filesLoading$?.subscribe(
			(filesLoading) => {
				this.filesLoading = filesLoading;
			}
		);
		this.filesSub = this.files$?.subscribe(
			(files) => {
				this.files = files;
				this.filesLoading = false;
			}
		);
		this.boardUserPagingSub = this.boardUserPaging$?.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					if (paging !== null && paging !== undefined) {
						this.totalRecords = paging.TotalCount;
					} else {
						this.totalRecords = 0;
					}
				}
			);
		this.offlineSub = this.offline$?.pipe(filter(d => !!d))
			.subscribe(
				(deviceStatus) => {
					this.offline = !deviceStatus.Online;
				}
			);
		this.taskRunningSub = this.taskRunning$.subscribe(
			(taskRunning) => {
				this.taskRunning = taskRunning;
			}
		);
	}

	ngOnDestroy(): void {
		this.boardUserPagingSub?.unsubscribe();
		this.pageSortSub?.unsubscribe();
	}

	getBoardUserList(): void {
		this.store.dispatch(UserFileActions.getBoarduserlists({ paging: this.paging }));
	}

	loadFiles(event: TableLazyLoadEvent) {
		this.filesLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.getBoardUserList();
	}

	importList(boarduserFile: UserFile): void {
		this.store.dispatch(UserFileActions.resetImportBoarduserListDialog());
		this.ref = this.dialogService.open(ImportFileDialogComponent, {
			header: this.translate.instant('IMPORT_LIST'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				boarduserFile,
			}
		});
	}

	editListFile(boarduserFile: UserFile): void {
		this.store.dispatch(UserFileActions.resetBoarduserListDialog());
		this.ref = this.dialogService.open(BoardUsersFilesDialogComponent, {
			header: this.translate.instant('EDIT_NAME') + ' ' + this.translate.instant('LIST').toLowerCase(),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				boarduserFile,
			}
		});
	}

	deleteListFile(boarduserFile: UserFile): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_HELP_ROW'),
			message: this.translate.instant('DELETE_HELP_ROW_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(UserFileActions.deleteBoarduserList({
					boarduserList: boarduserFile
				}));
			},
			reject: () => {
			}
		});
	}

	downloadUrl(url: string, fileName: string) {
		const a: any = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.style = 'display: none';
		a.click();
		a.remove();
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}